import React from "react";
import { Container, Card, Button, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import teamPerformanceImage from "../assets/what-is-shotarc/team-performance.png";
import playerAnalysisImage from "../assets/what-is-shotarc/player-analysis.png";
import gameByGameAnalysisImage from "../assets/what-is-shotarc/game-by-game-analysis.png";
import seasonAnalysisImage from "../assets/what-is-shotarc/season-analysis.png";

const WhatIsShotArcScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const sections = [
    {
      title: "What is ShotArc?",
      content:
        "ShotArc is an advanced shot analysis system in Gaelic Football to manage and analyze shots. It was developed by researchers at Dundalk Institute of Technology (DkIT), led by Dr Kevin McDaid. It provides a comprehensive platform to track shot data, visualize performance patterns, and gain insights into team and player shooting effectiveness. The core prediction model for shot success that forms the basis of the system is based on published research and allows for the variables including shot location, type and wind strength and direction. ",
    },
    {
      title: "How to Use ShotArc",
      content:
        "To start using ShotArc, begin by setting up your players in the squads page. You do not need to setup opposition players unless you want to analyse opposition shots by player. Next create a new game and then, while having the video for the game open in a separate window, enter the details of each shot including the location, type, and outcome. You are then in a position to analyse the performance of the team by game, player and over the season. There is a basic introductory video available at the Information screen once you login.",
    },
    {
      title: "Analysis & Insights",
      features: [
        {
          name: "Team Performance",
          description:
            "Analyze your team and the opposition's shooting efficiency by comparing the expected points (xP) with the actual points score for all shots.",
          image: teamPerformanceImage,
          imageAlt: "Team performance analysis view in ShotArc",
        },
        {
          name: "Game Performance",
          description:
            "Track the expected score and actual score through the game to provide deeper insights into performance.",
          image: gameByGameAnalysisImage,
          imageAlt: "Game-by-game analysis view in ShotArc",
        },
        {
          name: "Player Analysis",
          description:
            "Review individual player shooting profiles to understand strengths, weaknesses, and shooting patterns.",
          image: playerAnalysisImage,
          imageAlt: "Player analysis view in ShotArc",
        },
        {
          name: "Season Analysis",
          description:
            "Track shooting performance over entire seasons to monitor changes in shooting efficiency.",
          image: seasonAnalysisImage,
          imageAlt: "Season analysis view in ShotArc",
        },
      ],
    },
    {
      title: "Data Confidentiality & Research",
      content:
        "ShotArc is a research project developed by Dundalk Institute of Technology (DkIT). All shot information you enter is confidential. However, your data may be used on an anonymous basis by researchers to improve shot success prediction models, advancing the sport of Gaelic Football.",
    },
    {
      title: "Support",
      content:
        "Have any questions or experiencing problems logging in? Contact Kevin McDaid at kevin.mcdaid@dkit.ie for assistance.",
    },
  ];

  return (
    <Container className="pt-4 app-section-page">
      <title>What is ShotArc? | ShotArc</title>
      <h2 className="app-page-title info-page-title">What is ShotArc?</h2>

      <Card className="border-warning mb-4 info-shell">
        <Card.Header className="bg-warning text-dark">
          <strong>ShotArc Tutorial & System Overview</strong>
        </Card.Header>
        <Card.Body className="bg-dark text-white">
          {sections.map((section, idx) => (
            <section
              key={section.title}
              className={`info-section${idx < sections.length - 1 ? " info-section-divider" : ""}`}
            >
              <h5 className="info-section-title">{section.title}</h5>

              {section.features ? (
                <Row className="g-3">
                  {section.features.map((feature) => (
                    <Col md={6} key={feature.name}>
                      <div className="feature-item p-3 rounded">
                        <h6 className="text-warning mb-2">{feature.name}</h6>
                        <p className="mb-0 info-section-text">
                          {feature.description}
                        </p>
                        <div className="feature-image-scroll" role="region" aria-label={`${feature.name} screenshot preview`}>
                          <img
                            src={feature.image}
                            alt={feature.imageAlt}
                            className="feature-image"
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <p
                  className="mb-0 info-section-text"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {section.content}
                </p>
              )}
            </section>
          ))}
        </Card.Body>
      </Card>

      <div className="d-flex gap-2 justify-content-center mb-5">
        {!userInfo ? (
          <>
            <Button
              as={Link}
              to="/RegisterScreen"
              variant="warning"
              className="text-dark fw-semibold px-4"
            >
              Register Now
            </Button>
            <Button
              as={Link}
              to="/LoginScreen"
              variant="outline-warning"
              className="fw-semibold px-4"
            >
              Login
            </Button>
          </>
        ) : (
          <Button
            as={Link}
            to="/SquadDataScreen"
            variant="warning"
            className="text-dark fw-semibold px-4"
          >
            Go to Dashboard
          </Button>
        )}
      </div>
    </Container>
  );
};

export default WhatIsShotArcScreen;
